import React, { useEffect, useState } from 'react';
import { FiPlus, FiTrash2, FiShield, FiBell, FiMail, FiRefreshCw, FiAlertCircle, FiCheck } from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';
import { Card, Button, Input, Badge, Table, Modal } from '../../components/UI';
import { useAuth } from '../../context/AuthContext';
import { ADMIN_BOOTSTRAP_EMAILS, auth, db, doc, onSnapshot, setDoc, Timestamp } from '../../config/firebase';
import {
  listAdmins,
  grantAdmin,
  revokeAdmin,
  enrollTotp,
  confirmTotp,
  disableTotp,
  getTotpStatus,
  regenerateTotpCodes,
} from '../../services/adminService';
import styles from './Settings.module.css';

const Settings = () => {
  const { user, adminSource } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [hardcoded, setHardcoded] = useState(ADMIN_BOOTSTRAP_EMAILS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 2FA state
  const [totpStatus, setTotpStatus] = useState({ enabled: false, pending: false });
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollData, setEnrollData] = useState(null); // { secret, uri }
  const [enrollCode, setEnrollCode] = useState('');
  const [twoFaError, setTwoFaError] = useState('');
  const [twoFaInfo, setTwoFaInfo] = useState('');
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [regenCode, setRegenCode] = useState('');
  const [showRegenModal, setShowRegenModal] = useState(false);

  // Login notifications setting (lives in appSettings/adminLoginNotifications)
  const [loginNotifEnabled, setLoginNotifEnabled] = useState(true);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listAdmins();
      setAdmins(res.admins || []);
      setHardcoded(res.hardcoded || ADMIN_BOOTSTRAP_EMAILS);
    } catch (err) {
      console.error(err);
      setError(`Failed to load admins: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshTotp = async () => {
    try {
      const res = await getTotpStatus();
      setTotpStatus({ enabled: !!res.enabled, pending: !!res.pending });
    } catch (err) {
      console.error('totp status', err);
    }
  };

  useEffect(() => {
    refresh();
    refreshTotp();
  }, []);

  // Live subscribe to the login-notifications appSetting
  useEffect(() => {
    const ref = doc(db, 'appSettings', 'adminLoginNotifications');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const v = snap.data().value;
        setLoginNotifEnabled(v !== false);
      } else {
        setLoginNotifEnabled(true); // default on
      }
    });
    return () => unsub();
  }, []);

  const toggleLoginNotifs = async () => {
    const next = !loginNotifEnabled;
    try {
      await setDoc(
        doc(db, 'appSettings', 'adminLoginNotifications'),
        {
          value: next,
          type: 'boolean',
          description: 'Email all admins on each successful sign-in to the admin dashboard.',
          updatedAt: Timestamp.now(),
        },
        { merge: true }
      );
    } catch (err) {
      setError(`Could not update login notifications: ${err.message}`);
    }
  };

  const handleStartEnroll = async () => {
    setTwoFaError('');
    setTwoFaInfo('');
    setTwoFaLoading(true);
    try {
      const data = await enrollTotp();
      setEnrollData(data);
      setEnrollCode('');
      setShowEnroll(true);
    } catch (err) {
      setTwoFaError(`Enrollment failed: ${err.message}`);
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleConfirmEnroll = async () => {
    if (!enrollCode || enrollCode.replace(/\s/g, '').length < 6) return;
    setTwoFaLoading(true);
    setTwoFaError('');
    try {
      const result = await confirmTotp(enrollCode.trim());
      // confirmAdminTotp stamps the adminTotpAt claim; refresh the token to use it.
      await auth.currentUser?.getIdToken(true);
      setTwoFaInfo('Two-factor authentication enabled.');
      setShowEnroll(false);
      setEnrollData(null);
      setEnrollCode('');
      if (Array.isArray(result.recoveryCodes) && result.recoveryCodes.length) {
        setRecoveryCodes(result.recoveryCodes);
        setShowRecoveryModal(true);
      }
      refreshTotp();
    } catch (err) {
      setTwoFaError(err.message || 'Invalid code');
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleRegenerateRecoveryCodes = async () => {
    if (!regenCode || regenCode.replace(/\s/g, '').length < 6) return;
    setTwoFaLoading(true);
    setTwoFaError('');
    try {
      const result = await regenerateTotpCodes(regenCode.trim());
      setRecoveryCodes(result.recoveryCodes);
      setShowRegenModal(false);
      setRegenCode('');
      setShowRecoveryModal(true);
    } catch (err) {
      setTwoFaError(err.message || 'Failed to regenerate codes');
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleDisableTotp = async () => {
    if (!window.confirm('Disable 2FA on your account? You\'ll no longer be prompted for a code on sign-in.')) return;
    setTwoFaLoading(true);
    setTwoFaError('');
    try {
      await disableTotp();
      setTwoFaInfo('Two-factor authentication disabled.');
      refreshTotp();
    } catch (err) {
      setTwoFaError(err.message);
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleGrant = async () => {
    if (!pendingEmail || !pendingEmail.includes('@')) return;
    setSubmitting(true);
    setError('');
    setInfo('');
    try {
      const res = await grantAdmin({ email: pendingEmail });
      setInfo(`Granted admin to ${res.email}`);
      setPendingEmail('');
      refresh();
    } catch (err) {
      console.error(err);
      setError(`Grant failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (admin) => {
    if (hardcoded.includes((admin.email || '').toLowerCase())) {
      setError(
        `${admin.email} is in the bootstrap allowlist and cannot be revoked from the UI. Edit ADMIN_BOOTSTRAP_EMAILS in src/config/firebase.js to remove it.`
      );
      return;
    }
    if (!window.confirm(`Revoke admin access for ${admin.email || admin.uid}?`)) return;
    setSubmitting(true);
    setError('');
    setInfo('');
    try {
      await revokeAdmin({ uid: admin.uid });
      setInfo(`Revoked admin from ${admin.email || admin.uid}`);
      refresh();
    } catch (err) {
      console.error(err);
      setError(`Revoke failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const adminColumns = [
    {
      key: 'email',
      title: 'Email',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {row.avatar ? (
            <img
              src={row.avatar}
              alt=""
              style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0ea5e9, #10b981)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {(value || row.uid || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div style={{ color: '#f5f5f5' }}>{value || '(no email)'}</div>
            <div style={{ color: '#666', fontSize: 12 }}>UID: {row.uid}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'displayName',
      title: 'Name',
      render: (_, row) =>
        row.displayName ||
        [row.firstName, row.lastName].filter(Boolean).join(' ') ||
        '-',
    },
    {
      key: 'source',
      title: 'Source',
      render: (_, row) =>
        hardcoded.includes((row.email || '').toLowerCase()) ? (
          <Badge variant="primary">bootstrap</Badge>
        ) : (
          <Badge variant="secondary">isAdmin flag</Badge>
        ),
    },
    {
      key: 'actions',
      title: '',
      width: '120px',
      render: (_, row) => (
        <Button
          variant="danger"
          size="sm"
          icon={<FiTrash2 />}
          onClick={() => handleRevoke(row)}
          disabled={submitting || hardcoded.includes((row.email || '').toLowerCase())}
        >
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
        <p className={styles.subtitle}>Configure admin dashboard settings</p>
      </div>

      <div className={styles.grid}>
        <Card
          title="Admin Access"
          subtitle="Manage who can sign in and use this dashboard"
          action={
            <Button variant="ghost" size="sm" icon={<FiRefreshCw />} onClick={refresh} disabled={loading}>
              Refresh
            </Button>
          }
        >
          <div className={styles.section}>
            <p className={styles.sectionDescription}>
              Admin access is granted by either being on the bootstrap email allowlist OR having
              <code> users/{'{uid}'}.isAdmin == true</code> in Firestore. Granting a new admin below sets
              both that flag and the Firebase Auth custom claim.
            </p>

            {error && (
              <div className={styles.errorBox}>
                <FiAlertCircle />
                <span>{error}</span>
              </div>
            )}
            {info && (
              <div className={styles.infoBox}>
                <span>{info}</span>
              </div>
            )}

            <div className={styles.addEmail}>
              <Input
                placeholder="Enter user email to grant admin..."
                value={pendingEmail}
                onChange={(e) => setPendingEmail(e.target.value)}
                icon={FiMail}
              />
              <Button
                variant="primary"
                icon={<FiPlus />}
                onClick={handleGrant}
                loading={submitting}
                disabled={!pendingEmail || !pendingEmail.includes('@')}
              >
                Grant Admin
              </Button>
            </div>

            <div style={{ marginTop: 24 }}>
              <Table
                columns={adminColumns}
                data={admins}
                loading={loading}
                emptyMessage="No admins flagged via users.isAdmin"
              />
            </div>

            <div className={styles.bootstrapList}>
              <h4>Bootstrap allowlist (always granted)</h4>
              <ul>
                {hardcoded.map((e) => (
                  <li key={e}>
                    <FiMail /> <span>{e}</span>
                  </li>
                ))}
              </ul>
              <p className={styles.note}>
                Edit <code>admin/src/config/firebase.js</code> and{' '}
                <code>functions/index.js → ADMIN_PANEL_EMAILS</code> to add or remove emails from the
                bootstrap allowlist (these don't require database flags).
              </p>
            </div>
          </div>
        </Card>

        <Card title="Security" subtitle="Two-factor authentication & login alerts">
          <div className={styles.section}>
            {twoFaError && (
              <div className={styles.errorBox}>
                <FiAlertCircle />
                <span>{twoFaError}</span>
              </div>
            )}
            {twoFaInfo && (
              <div className={styles.infoBox}>
                <span>{twoFaInfo}</span>
              </div>
            )}

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <FiShield className={styles.settingIcon} />
                <div>
                  <span className={styles.settingTitle}>Two-Factor Authentication (TOTP)</span>
                  <span className={styles.settingDescription}>
                    {totpStatus.enabled
                      ? 'Active. You\'ll be prompted for a code on every sign-in.'
                      : 'Use Google Authenticator, 1Password, or any TOTP app to add a second factor.'}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {totpStatus.enabled ? (
                  <>
                    <Badge variant="success">enabled</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRegenCode('');
                        setShowRegenModal(true);
                      }}
                      disabled={twoFaLoading}
                    >
                      New recovery codes
                    </Button>
                    <Button variant="danger" size="sm" onClick={handleDisableTotp} disabled={twoFaLoading}>
                      Disable
                    </Button>
                  </>
                ) : (
                  <Button variant="primary" size="sm" onClick={handleStartEnroll} disabled={twoFaLoading}>
                    Enable 2FA
                  </Button>
                )}
              </div>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <FiBell className={styles.settingIcon} />
                <div>
                  <span className={styles.settingTitle}>Login Notifications</span>
                  <span className={styles.settingDescription}>
                    Email all admins on each successful sign-in to this dashboard.
                  </span>
                </div>
              </div>
              <button
                className={`${styles.toggle} ${loginNotifEnabled ? styles.toggleOn : ''}`}
                onClick={toggleLoginNotifs}
                aria-label={loginNotifEnabled ? 'on' : 'off'}
              >
                <span className={styles.knob} />
              </button>
            </div>
          </div>
        </Card>

        <Modal
          isOpen={showEnroll}
          onClose={() => setShowEnroll(false)}
          title="Enable Two-Factor Authentication"
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowEnroll(false)}>Cancel</Button>
              <Button
                variant="primary"
                icon={<FiCheck />}
                onClick={handleConfirmEnroll}
                loading={twoFaLoading}
                disabled={enrollCode.replace(/\s/g, '').length < 6}
              >
                Verify & enable
              </Button>
            </>
          }
        >
          {enrollData && (
            <div className={styles.enroll}>
              <p>1. Scan the QR code with your authenticator app:</p>
              <div className={styles.qrWrap}>
                <QRCodeSVG value={enrollData.uri} size={180} bgColor="#ffffff" fgColor="#0a0a0a" />
              </div>
              <p>
                Or enter this secret manually:
                <br />
                <code className={styles.secret}>{enrollData.secret}</code>
              </p>
              <p>2. Enter the 6-digit code shown in your app to confirm:</p>
              <Input
                placeholder="123 456"
                inputMode="numeric"
                value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value.replace(/[^\d ]/g, ''))}
                autoFocus
              />
              {twoFaError && <div className={styles.errorBox}><FiAlertCircle /><span>{twoFaError}</span></div>}
            </div>
          )}
        </Modal>

        <Modal
          isOpen={showRecoveryModal}
          onClose={() => setShowRecoveryModal(false)}
          title="Save your recovery codes"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  if (recoveryCodes) {
                    navigator.clipboard?.writeText(recoveryCodes.join('\n'));
                  }
                }}
              >
                Copy to clipboard
              </Button>
              <Button variant="primary" onClick={() => setShowRecoveryModal(false)}>
                I have saved them
              </Button>
            </>
          }
        >
          <div className={styles.recovery}>
            <div className={styles.errorBox}>
              <FiAlertCircle />
              <span>
                Save these codes <strong>now</strong>. You won't see them again. Each code can be used
                exactly once if you lose access to your authenticator.
              </span>
            </div>
            <div className={styles.recoveryGrid}>
              {(recoveryCodes || []).map((c) => (
                <code key={c} className={styles.recoveryCode}>{c}</code>
              ))}
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={showRegenModal}
          onClose={() => setShowRegenModal(false)}
          title="Regenerate recovery codes"
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowRegenModal(false)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleRegenerateRecoveryCodes}
                loading={twoFaLoading}
                disabled={regenCode.replace(/\s/g, '').length < 6}
              >
                Regenerate
              </Button>
            </>
          }
        >
          <div className={styles.enroll}>
            <p>
              This invalidates your existing recovery codes. Enter the current code from your
              authenticator app to confirm.
            </p>
            <Input
              placeholder="123 456"
              inputMode="numeric"
              value={regenCode}
              onChange={(e) => setRegenCode(e.target.value.replace(/[^\d ]/g, ''))}
              autoFocus
            />
            {twoFaError && <div className={styles.errorBox}><FiAlertCircle /><span>{twoFaError}</span></div>}
          </div>
        </Modal>

        <Card title="About" subtitle="System information">
          <div className={styles.aboutContent}>
            <div className={styles.aboutItem}>
              <span className={styles.aboutLabel}>Logged in as</span>
              <span className={styles.aboutValue}>{user?.email || '-'}</span>
            </div>
            <div className={styles.aboutItem}>
              <span className={styles.aboutLabel}>Auth source</span>
              <span className={styles.aboutValue}>
                <Badge variant="primary">{adminSource || '-'}</Badge>
              </span>
            </div>
            <div className={styles.aboutItem}>
              <span className={styles.aboutLabel}>Firebase Project</span>
              <span className={styles.aboutValue}>dierend-e3e72</span>
            </div>
            <div className={styles.aboutItem}>
              <span className={styles.aboutLabel}>Hosting Site</span>
              <span className={styles.aboutValue}>admin-dierend</span>
            </div>
            <div className={styles.aboutItem}>
              <span className={styles.aboutLabel}>Functions Region</span>
              <span className={styles.aboutValue}>us-central1 (default)</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
